import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Pencil, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Bulk Support Contact Editor
 * Select assignments → set support contact name/email/phone
 */
export function BulkSupportContactDialog({ open, onOpenChange, token, items, onComplete }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const toggleAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((_, i) => i)));
  };
  const toggle = (i) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };

  const save = async () => {
    const keys = Array.from(selectedIds).map(i => items[i]).map(it => ({
      property_id: it.property_id,
      platform_type_id: it.platform_id || it.platform_type_id,
    }));
    if (!keys.length) return;
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/bulk/support-contacts`, {
        assignment_keys: keys,
        support_contact_name: name || undefined,
        support_contact_email: email || undefined,
        support_contact_phone: phone || undefined,
      }, { headers });
      toast.success(`Updated ${res.data.updated} assignments`);
      onComplete?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Set Support Contacts</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Set the same support contact info on multiple platforms at once.</p>

        <div className="grid grid-cols-3 gap-3 py-2">
          <div><label className="text-xs font-medium">Contact Name</label><Input value={name} onChange={e => setName(e.target.value)} placeholder="IT Help Desk" className="mt-1 h-8 text-sm" data-testid="bulk-contact-name" /></div>
          <div><label className="text-xs font-medium">Email</label><Input value={email} onChange={e => setEmail(e.target.value)} placeholder="support@hotel.com" className="mt-1 h-8 text-sm" data-testid="bulk-contact-email" /></div>
          <div><label className="text-xs font-medium">Phone</label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="555-0100" className="mt-1 h-8 text-sm" data-testid="bulk-contact-phone" /></div>
        </div>

        <Separator />

        <div className="flex items-center justify-between py-1">
          <span className="text-xs text-muted-foreground">{selectedIds.size} of {items.length} selected</span>
          <Button variant="ghost" size="sm" onClick={toggleAll} className="text-[10px] h-6">{selectedIds.size === items.length ? 'Deselect All' : 'Select All'}</Button>
        </div>

        <ScrollArea className="flex-1 max-h-[300px]">
          <div className="space-y-1">
            {items.map((it, i) => (
              <label key={i} className="flex items-center gap-2 p-2 rounded hover:bg-muted/40 cursor-pointer text-sm">
                <Checkbox checked={selectedIds.has(i)} onCheckedChange={() => toggle(i)} />
                <span className="font-medium w-36 truncate">{it.property_name}</span>
                <Badge variant="outline" className="text-[9px] shrink-0">{it.platform || it.platform_name}</Badge>
                <span className="text-xs text-muted-foreground truncate ml-auto">{it.product || it.product_name || ''}</span>
              </label>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || selectedIds.size === 0 || (!name && !email && !phone)} data-testid="bulk-save-contacts">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Update {selectedIds.size} Assignments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Bulk Governance Editor
 * Select assignments → set platform_admin, tech_support, auth_method
 */
export function BulkGovernanceDialog({ open, onOpenChange, token, items, onComplete }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [platformAdmin, setPlatformAdmin] = useState('');
  const [techSupport, setTechSupport] = useState('');
  const [authMethod, setAuthMethod] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const toggleAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((_, i) => i)));
  };
  const toggle = (i) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };

  const save = async () => {
    const keys = Array.from(selectedIds).map(i => items[i]).map(it => ({
      property_id: it.property_id,
      platform_type_id: it.platform_id || it.platform_type_id,
    }));
    if (!keys.length) return;
    setSaving(true);
    try {
      const body = { assignment_keys: keys };
      if (platformAdmin) body.platform_admin = platformAdmin;
      if (techSupport) body.tech_support = techSupport;
      if (authMethod) body.auth_method = authMethod;
      const res = await axios.put(`${BACKEND_URL}/api/admin/bulk/governance`, body, { headers });
      toast.success(`Updated ${res.data.updated} assignments`);
      onComplete?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Set Governance</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Set governance ownership and auth method on multiple platforms at once.</p>

        <div className="grid grid-cols-3 gap-3 py-2">
          <div>
            <label className="text-xs font-medium">Platform Admin</label>
            <Select value={platformAdmin} onValueChange={setPlatformAdmin}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="corporate">Corporate</SelectItem>
                <SelectItem value="property">Property</SelectItem>
                <SelectItem value="third_party">Third Party</SelectItem>
                <SelectItem value="brand">Brand</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">Tech Support</label>
            <Select value={techSupport} onValueChange={setTechSupport}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="corporate">Corporate IT</SelectItem>
                <SelectItem value="property">Property Staff</SelectItem>
                <SelectItem value="vendor">Vendor Support</SelectItem>
                <SelectItem value="third_party">Third Party MSP</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">Auth Method</label>
            <Select value={authMethod} onValueChange={setAuthMethod}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sso">SSO</SelectItem>
                <SelectItem value="standalone">Standalone</SelectItem>
                <SelectItem value="shared">Shared Credentials</SelectItem>
                <SelectItem value="api_key">API Key</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        <div className="flex items-center justify-between py-1">
          <span className="text-xs text-muted-foreground">{selectedIds.size} of {items.length} selected</span>
          <Button variant="ghost" size="sm" onClick={toggleAll} className="text-[10px] h-6">{selectedIds.size === items.length ? 'Deselect All' : 'Select All'}</Button>
        </div>

        <ScrollArea className="flex-1 max-h-[300px]">
          <div className="space-y-1">
            {items.map((it, i) => (
              <label key={i} className="flex items-center gap-2 p-2 rounded hover:bg-muted/40 cursor-pointer text-sm">
                <Checkbox checked={selectedIds.has(i)} onCheckedChange={() => toggle(i)} />
                <span className="font-medium w-36 truncate">{it.property_name}</span>
                <Badge variant="outline" className="text-[9px] shrink-0">{it.platform || it.platform_name}</Badge>
              </label>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || selectedIds.size === 0 || (!platformAdmin && !techSupport && !authMethod)} data-testid="bulk-save-governance">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Update {selectedIds.size} Assignments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Bulk Fill Gaps Dialog
 * Shows missing platforms for a property, lets user select and assign solutions.
 */
export function BulkFillGapsDialog({ open, onOpenChange, token, propertyId, propertyName, onComplete }) {
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Load gaps when dialog opens
  React.useEffect(() => {
    if (open && propertyId) {
      setLoading(true);
      setSelectedIds(new Set());
      axios.get(`${BACKEND_URL}/api/admin/property-gaps/${propertyId}`, { headers })
        .then(res => setGaps(res.data.gaps || []))
        .catch(() => toast.error('Failed to load gaps'))
        .finally(() => setLoading(false));
    }
  }, [open, propertyId, headers]);

  const toggleAll = () => {
    if (selectedIds.size === gaps.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(gaps.map((_, i) => i)));
  };
  const toggle = (i) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };

  const save = async () => {
    const platforms = Array.from(selectedIds).map(i => {
      const g = gaps[i];
      const firstProd = g.suggested_products?.[0];
      return {
        platform_type_id: g.platform_id,
        vendor_name: firstProd?.vendor || '',
        product_id: firstProd?.id || '',
      };
    });
    if (!platforms.length) return;
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/assignments/fill-gaps`, {
        property_id: propertyId,
        platforms,
      }, { headers });
      toast.success(`Assigned ${res.data.created} platforms to ${propertyName}`);
      onComplete?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
    finally { setSaving(false); }
  };

  // Group by category
  const grouped = useMemo(() => {
    const g = {};
    gaps.forEach((gap, i) => {
      const cat = gap.category_name || 'Other';
      if (!g[cat]) g[cat] = { color: gap.category_color, items: [] };
      g[cat].items.push({ ...gap, _idx: i });
    });
    return g;
  }, [gaps]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Fill Coverage Gaps — {propertyName}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Select platforms to assign. Top suggested products will be auto-assigned where available.</p>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-muted-foreground">{selectedIds.size} of {gaps.length} gaps selected</span>
              <Button variant="ghost" size="sm" onClick={toggleAll} className="text-[10px] h-6">{selectedIds.size === gaps.length ? 'Deselect All' : 'Select All'}</Button>
            </div>

            <ScrollArea className="flex-1 max-h-[400px]">
              <div className="space-y-4">
                {Object.entries(grouped).map(([cat, { color, items }]) => (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{cat}</span>
                      <Badge variant="secondary" className="text-[9px]">{items.length}</Badge>
                    </div>
                    <div className="space-y-1">
                      {items.map(g => (
                        <label key={g._idx} className="flex items-center gap-2.5 p-2 rounded hover:bg-muted/40 cursor-pointer text-sm border">
                          <Checkbox checked={selectedIds.has(g._idx)} onCheckedChange={() => toggle(g._idx)} />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{g.platform_name}</span>
                            {g.importance >= 2 && <Badge className="ml-1.5 text-[8px] bg-amber-100 text-amber-700">Important</Badge>}
                          </div>
                          {g.suggested_products?.length > 0 && (
                            <span className="text-[10px] text-muted-foreground truncate max-w-[180px]">
                              Suggest: {g.suggested_products[0].name} ({g.suggested_products[0].vendor})
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving || selectedIds.size === 0} data-testid="bulk-fill-gaps-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Assign {selectedIds.size} Platforms
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
